/* eslint-disable react-hooks/set-state-in-effect -- layout effect publishes one-shot focusBeerId on profile return and brewery switch */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface UsePintPortfolioCatalogFocusRestoreOptions {
  isProfileRoute: boolean;
  profileBeerId: string;
  breweryId: string | null | undefined;
}

export interface UsePintPortfolioCatalogFocusRestoreResult {
  focusBeerId: string;
  clearFocusBeerId: () => void;
}

/**
 * Remembers the beer profile the operator opened and exposes a one-shot beer id
 * for the catalog grid to scroll back into view on return.
 */
export function usePintPortfolioCatalogFocusRestore({
  isProfileRoute,
  profileBeerId,
  breweryId,
}: UsePintPortfolioCatalogFocusRestoreOptions): UsePintPortfolioCatalogFocusRestoreResult {
  const lastViewedBeerIdRef = useRef('');
  const wasProfileRouteRef = useRef(isProfileRoute);
  const previousBreweryIdRef = useRef(breweryId);
  const [focusBeerId, setFocusBeerId] = useState('');

  useEffect(() => {
    if (isProfileRoute && profileBeerId.length > 0) {
      lastViewedBeerIdRef.current = profileBeerId;
    }
  }, [isProfileRoute, profileBeerId]);

  useLayoutEffect(() => {
    const breweryChanged = previousBreweryIdRef.current !== breweryId;
    if (breweryChanged) {
      lastViewedBeerIdRef.current = '';
      previousBreweryIdRef.current = breweryId;
      setFocusBeerId('');
    }

    const wasProfileRoute = wasProfileRouteRef.current;
    wasProfileRouteRef.current = isProfileRoute;

    if (
      !breweryChanged &&
      wasProfileRoute &&
      !isProfileRoute &&
      lastViewedBeerIdRef.current.length > 0
    ) {
      setFocusBeerId(lastViewedBeerIdRef.current);
    }
  }, [isProfileRoute, breweryId]);

  const clearFocusBeerId = useCallback(() => {
    setFocusBeerId('');
  }, []);

  return { focusBeerId, clearFocusBeerId };
}
